import React, { useCallback, useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useCorporate } from "../CorporateContext";
import { getClientsDashboard } from "../../../../Services/adminReporting";
import { Card, CardContent } from "../../../../ui/card";
import { Skeleton } from "../../../../ui/skeleton";
import { Separator } from "../../../../ui/separator";
import {
  FunnelSteps,
  RankedBars,
  SectionHeader,
  StatCard,
  SuppressionNotice,
  TrendDelta,
  WindowToggle,
} from "../../../../Components/admin/reporting/primitives";
import {
  CARD_CLASS,
  ComparePeriodBar,
  CustomRangePopover,
  DashboardSurface,
  DeltaBadge,
  DemographicsPopover,
  ElementFunnel,
  Panel,
  Toolbar,
  periodRange,
} from "../../../../Components/admin/reporting/controls";
import { cn } from "../../../../lib/utils";

const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString());

const CmpStat = ({ label, a, b, fmt = fmtNum, goodWhenDown = false }) => (
  <Card className={CARD_CLASS}>
    <CardContent className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-3">
        <span className="text-xl font-semibold tabular-nums text-sky-600">{fmt(a)}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-xl font-semibold tabular-nums text-amber-600">{fmt(b)}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 border-t pt-1.5">
        <DeltaBadge a={a} b={b} goodWhenDown={goodWhenDown} />
        <span className="text-[10px] uppercase text-muted-foreground">Δ A vs B</span>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { corporate } = useCorporate();
  const [windowDays, setWindowDays] = useState(30);
  const [range, setRange] = useState(null);
  const [demoPredicate, setDemoPredicate] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [periodA, setPeriodA] = useState("this_month");
  const [periodB, setPeriodB] = useState("last_month");

  const [data, setData] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const corporateId = corporate?.id || corporate?._id;

  const load = useCallback(async () => {
    if (!corporateId) return;
    setLoading(true);
    setError(null);
    try {
      if (compareMode) {
        const [a, b] = await Promise.all([
          getClientsDashboard(corporateId, windowDays, demoPredicate, periodRange(periodA)),
          getClientsDashboard(corporateId, windowDays, demoPredicate, periodRange(periodB)),
        ]);
        setCmp({ a, b });
      } else {
        setData(await getClientsDashboard(corporateId, windowDays, demoPredicate, range));
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load the dashboard.");
    } finally {
      setLoading(false);
    }
  }, [corporateId, windowDays, demoPredicate, range, compareMode, periodA, periodB]);

  useEffect(() => {
    load();
  }, [load]);

  // demographic_options come from whichever payload we have
  const options = data?.demographic_options || cmp?.a?.demographic_options;

  const Controls = (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionHeader
          title="Client dashboard"
          hint={
            !compareMode && data?.window
              ? `${data.window.start} → ${data.window.end}`
              : compareMode
              ? `${periodRange(periodA).label} vs ${periodRange(periodB).label}`
              : ""
          }
        />
        {!compareMode ? (
          <WindowToggle
            value={range ? null : windowDays}
            onChange={(d) => {
              setRange(null);
              setWindowDays(d);
            }}
          />
        ) : null}
      </div>
      <Toolbar>
        {!compareMode ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Days
            </span>
            <CustomRangePopover active={range} onApply={setRange} onClear={() => setRange(null)} />
          </div>
        ) : null}
        <DemographicsPopover options={options} value={demoPredicate} onChange={setDemoPredicate} />
        <button
          type="button"
          onClick={() => setCompareMode((m) => !m)}
          className={cn(
            "ml-auto rounded-full border px-3 py-1 text-xs font-medium",
            compareMode
              ? "border-amber-500 bg-amber-50 text-amber-700"
              : "border-amber-400/60 text-amber-700 hover:bg-amber-50"
          )}
        >
          ⇄ Compare periods
        </button>
      </Toolbar>
      {compareMode ? (
        <ComparePeriodBar
          a={periodA}
          b={periodB}
          onChange={(a, b) => {
            setPeriodA(a);
            setPeriodB(b);
          }}
          onClose={() => setCompareMode(false)}
        />
      ) : null}
      {demoPredicate ? (
        <p className="text-xs text-muted-foreground">
          Filtered to{" "}
          <span className="font-medium">
            {(data || cmp?.a)?.filter?.value || "selected demographics"}
          </span>
          . Adoption uses this slice; benefits stay org-wide.
        </p>
      ) : null}
    </div>
  );

  if (loading && !data && !cmp) {
    return (
      <DashboardSurface fillViewport={false}>
        <div className="space-y-4">
          {Controls}
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </DashboardSurface>
    );
  }

  if (error) {
    return (
      <DashboardSurface fillViewport={false}>
        <div className="space-y-4">
          {Controls}
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        </div>
      </DashboardSurface>
    );
  }

  // ── Compare mode ──────────────────────────────────────────────────────────
  if (compareMode && cmp) {
    const a = cmp.a;
    const b = cmp.b;
    const ea = a?.engagement || {};
    const eb = b?.engagement || {};
    return (
      <DashboardSurface fillViewport={false}>
       <div className="space-y-7">
        {Controls}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <CmpStat label="Active users" a={ea.active_users} b={eb.active_users} />
          <CmpStat label="Sessions" a={ea.sessions} b={eb.sessions} />
          <CmpStat
            label="Activation"
            a={a?.adoption?.activation_rate}
            b={b?.adoption?.activation_rate}
            fmt={(v) => (v == null ? "—" : `${Math.round(v * 100)}%`)}
          />
          <CmpStat label="Returning users" a={ea.returning_users} b={eb.returning_users} />
          <CmpStat label="Sessions / user" a={ea.sessions_per_active_user} b={eb.sessions_per_active_user} fmt={(v) => (v == null ? "—" : v)} />
          <CmpStat label="Median session (min)" a={ea.median_session_minutes} b={eb.median_session_minutes} fmt={(v) => (v == null ? "—" : v)} />
        </div>
        <Panel
          title="Element completion"
          right={<span className="text-xs text-muted-foreground">{periodRange(periodA).label} vs {periodRange(periodB).label}</span>}
        >
          <ElementFunnel data={{ a: a.element_completion, b: b.element_completion }} compare />
        </Panel>
       </div>
      </DashboardSurface>
    );
  }

  if (!data) return null;

  const {
    adoption = {},
    engagement = {},
    quality = {},
    usage_mix: usageMix = {},
    top_issues: topIssues = {},
    benefits_recommended: benefits = [],
    issue_benefit_ribbon: ribbon = {},
    daily_trend: dailyTrend = [],
    element_completion: elements,
  } = data;

  const prev = engagement.previous || {};
  const totalMix =
    (usageMix.voice_sessions || 0) + (usageMix.text_sessions || 0) + (usageMix.dictation_sessions || 0);

  return (
    <DashboardSurface fillViewport={false}>
     <div className="space-y-7">
      {Controls}

      {/* Headline */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Active users" value={engagement.active_users} trend={<TrendDelta current={engagement.active_users} previous={prev.active_users} />} />
        <StatCard label="Sessions" value={engagement.sessions} trend={<TrendDelta current={engagement.sessions} previous={prev.sessions} />} />
        <StatCard
          label="Activation rate"
          value={adoption.activation_rate != null ? `${Math.round(adoption.activation_rate * 100)}%` : "—"}
          sub={`${adoption.first_session ?? 0} of ${adoption.seats_enrolled || adoption.members_total || 0} seats`}
        />
        <StatCard
          label="Avg rating"
          value={quality.suppressed ? "—" : quality.avg_rating ?? "—"}
          sub={quality.suppressed ? "Hidden (small group)" : `${quality.rating_count} ratings`}
        />
      </div>

      {/* Adoption */}
      <Panel title="Adoption">
        <FunnelSteps
          steps={[
            { label: "Enrolled members", count: adoption.members_total ?? 0 },
            { label: "Registered", count: adoption.registered ?? 0 },
            { label: "Started onboarding", count: adoption.onboarding_started ?? 0 },
            { label: "Completed onboarding", count: adoption.onboarding_completed ?? 0 },
            { label: "Started first session", count: adoption.first_session ?? 0 },
          ]}
        />
        {adoption.median_days_to_first_session != null ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Median time from onboarding completion to first session: {adoption.median_days_to_first_session} days
          </p>
        ) : null}
      </Panel>

      {/* Element completion */}
      <Panel title="Element completion" right={<span className="text-xs text-muted-foreground">of enrolled members</span>}>
        <ElementFunnel data={elements} />
      </Panel>

      {/* Engagement */}
      <Panel title="Engagement">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Sessions / active user" value={engagement.sessions_per_active_user ?? "—"} />
          <StatCard label="Returning users" value={engagement.returning_users} trend={<TrendDelta current={engagement.returning_users} previous={prev.returning_users} />} />
          <StatCard label="Median session" value={engagement.median_session_minutes != null ? `${engagement.median_session_minutes}m` : "—"} />
          <StatCard
            label="Voice share"
            value={totalMix ? `${Math.round(((usageMix.voice_sessions || 0) / totalMix) * 100)}%` : "—"}
            sub={`${usageMix.voice_sessions || 0} voice / ${usageMix.text_sessions || 0} text${usageMix.dictation_sessions ? ` / ${usageMix.dictation_sessions} dictation` : ""}`}
          />
        </div>
        {dailyTrend.length > 1 ? (
          <div className="mt-4 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
                <Tooltip />
                <Line type="monotone" dataKey="sessions" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Sessions" />
                <Line type="monotone" dataKey="active_users" stroke="#10b981" strokeWidth={2} dot={false} name="Active users" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </Panel>

      {/* Top issues */}
      <Panel title="Top issues">
        {topIssues.suppressed ? (
          <SuppressionNotice>
            Hidden until this organization reaches {topIssues.min_sessions ?? 20} sessions in the
            selected window — protects individual privacy in small groups.
          </SuppressionNotice>
        ) : (
          <RankedBars items={(topIssues.topics || []).map((t) => ({ label: t.topic, count: t.count }))} emptyLabel="No tagged sessions in this window yet." />
        )}
      </Panel>

      {/* Benefits */}
      <Panel
        title="Benefits recommended"
        right={data.benefits_is_global ? <span className="text-xs text-muted-foreground">org-wide (not filtered)</span> : null}
      >
        <RankedBars
          color="bg-violet-500/70"
          items={(benefits || []).map((bf) => ({
            label: bf.name || bf.benefit_id,
            count: bf.count,
            suffix: bf.count ? `${Math.round(((bf.accepted || 0) / bf.count) * 100)}% accepted` : "never offered",
          }))}
          emptyLabel="No benefits configured or recommended yet."
        />
      </Panel>

      {/* Issue → benefit */}
      <Panel title="Issue → benefit matching">
        {ribbon.suppressed ? (
          <SuppressionNotice>Hidden until this organization reaches enough sessions in the selected window.</SuppressionNotice>
        ) : (ribbon.pairs || []).length ? (
          <div className="space-y-1.5">
            {ribbon.pairs.map((p) => (
              <div key={`${p.topic}-${p.benefit_id}`} className="flex items-center gap-2 text-sm">
                <span className="w-44 shrink-0 truncate" title={p.topic}>{p.topic}</span>
                <span className="text-muted-foreground">→</span>
                <span className="flex-1 truncate" title={p.benefit_name}>{p.benefit_name}</span>
                <span className="tabular-nums text-muted-foreground">{p.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">No issue→benefit links in this window yet.</p>
        )}
      </Panel>

      <Separator />
      <p className="text-xs text-muted-foreground">
        All metrics are aggregate and category-level. No transcripts, quotes, per-employee data, or
        safety signals are ever shown. Data refreshes daily.
      </p>
     </div>
    </DashboardSurface>
  );
};

export default Dashboard;
